import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import navLogoSrc from '../../assets/mywellfie-header-logo.png';
import reportLogoSrc from '../../assets/mywellfie-full-logo.png';
import { resolveAdhocId } from '../../config/adhocTiming';
import {
  createAdhocCheckout,
  createAdhocSession,
  getAdhocConfig,
  type AdhocBoothConfig,
} from '../../api/adhocApi';
import AdhocPaymentMethodButtons, { type PaymentProvider } from './AdhocPaymentMethodButtons';
import { savePendingGuestCheckout } from './adhocPackStorage';
import { ADHOC_SCAN_MAINTENANCE } from '../../config/featureFlags';
import AdhocMaintenanceStage from './AdhocMaintenanceStage';
import './landing.css';

export default function AdhocSingleScanCheckoutPage() {
  const adhocId = resolveAdhocId(window.location.search);
  const displayPath = `/display?adhoc_id=${encodeURIComponent(adhocId)}`;

  const [boothConfig, setBoothConfig] = useState<AdhocBoothConfig | null>(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  useEffect(() => {
    setPaying(false);
  }, []);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setPaying(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    getAdhocConfig(adhocId)
      .then(setBoothConfig)
      .catch(() => setBoothConfig(null));
  }, [adhocId]);

  const handlePay = useCallback(async (provider: PaymentProvider) => {
    if (paying) return;
    setPaying(true);
    setPayError(null);
    try {
      const session = await createAdhocSession(adhocId);
      const { checkout_url, checkout_id, provider: checkoutProvider } = await createAdhocCheckout(
        session.id,
        { provider },
      );
      savePendingGuestCheckout(adhocId, session.id, checkout_id, checkoutProvider);
      window.location.replace(checkout_url);
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail;
      setPayError(typeof detail === 'string' ? detail : 'Could not start checkout.');
      setPaying(false);
    }
  }, [adhocId, paying]);

  const priceDisplay = boothConfig?.price_display ?? 'USD $3';
  const compareAtDisplay = boothConfig?.compare_at_display ?? 'USD $15';

  if (ADHOC_SCAN_MAINTENANCE) {
    return (
      <AdhocMaintenanceStage
        paidAfterCheckout={false}
        onReturn={() => { window.location.href = displayPath; }}
      />
    );
  }

  return (
    <div className="adhoc-landing">
      {paying && (
        <div className="pay-overlay" aria-live="polite">
          <div className="pay-overlay-spinner" />
          <p>Preparing secure checkout…</p>
        </div>
      )}

      <nav>
        <div className="wrap">
          <div className="nav-row-split">
            <div className="nav-logo">
              <img src={navLogoSrc} alt="MyWellfie — Smart scans. Health insights." />
            </div>
            <Link to={displayPath} className="btn btn-ghost family-pack-nav-back">
              <ArrowLeft size={16} aria-hidden />
              Back
            </Link>
          </div>
        </div>
      </nav>

      <main className="family-pack-page-main">
        <div className="wrap family-pack-page-inner">
          <span className="eyebrow">Single scan</span>
          <h1>Get 1 scan — {priceDisplay}</h1>
          <p className="family-pack-page-lede">
            One face scan with a full personal health report emailed to you.
            {compareAtDisplay ? ` Introductory price until our official launch (normally ${compareAtDisplay} per scan).` : ''}
          </p>

          <div className="family-pack-custom family-pack-buy-single">
            <AdhocPaymentMethodButtons paying={paying} onPay={handlePay} />
          </div>

          {payError && (
            <p className="family-pack-error" role="alert">
              {payError}
            </p>
          )}
        </div>
      </main>

      <footer>
        <div className="wrap">
          <div className="brand">
            <img src={reportLogoSrc} alt="MyWellfie" />
          </div>
          <span className="fine">
            MyWellfie reports are for informational purposes only and don&apos;t replace
            professional medical advice.
          </span>
        </div>
      </footer>
    </div>
  );
}
